import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, HelpCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const pricingTiers = [
  {
    title: "Launch & Validation",
    range: "$8,000 - $15,000",
    timeline: "3-6 weeks",
    description: "Know if your idea is real before committing everything.",
    includes: [
      "Problem validation",
      "Customer discovery calls",
      "Pricing validation",
      "Minimum viable product",
      "Go/no-go recommendation",
    ],
    ideal: "First-time founders with an idea to test.",
  },
  {
    title: "Product Execution",
    range: "$12,000 - $25,000/mo",
    timeline: "Ongoing",
    description: "Build and scale with dedicated execution support.",
    includes: [
      "Product development",
      "Technical execution",
      "Weekly strategy calls",
      "Operational support",
      "Growth guidance",
    ],
    ideal: "Founders who have validated and need to scale.",
    popular: true,
  },
  {
    title: "Custom Build",
    range: "$20,000 - $75,000+",
    timeline: "Varies",
    description: "Specific product or tool development.",
    includes: [
      "Scoped project work",
      "Custom SaaS development",
      "Internal tools",
      "AI integration",
      "Full documentation",
    ],
    ideal: "Founders who know exactly what they need built.",
  },
];

const faq = [
  {
    question: "Why ranges instead of fixed prices?",
    answer: "Every founder situation is different. Ranges let us scope to your specific needs rather than forcing a one-size-fits-all package. After our initial conversation, you will get a clear, fixed quote.",
  },
  {
    question: "What is included in these prices?",
    answer: "Everything needed for the engagement: our time, expertise, execution, and any tools we use. No hidden fees, no surprise add-ons.",
  },
  {
    question: "Is this expensive compared to alternatives?",
    answer: "Compared to a cheap freelancer? Yes. Compared to the cost of building the wrong thing? No. We are priced for founders who understand that clarity is worth paying for.",
  },
  {
    question: "Do you offer payment plans?",
    answer: "For larger engagements, yes. We typically structure payments around milestones so you are paying as value is delivered.",
  },
  {
    question: "What if it does not work out?",
    answer: "We structure engagements in short cycles. If things are not working, we can adjust or conclude without you being locked into a long contract.",
  },
];

// Chaos vs Clarity Scale illustration - showing value of clarity
const illustration = "/images/pricing.png";

export default function PricingPage() {
  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="bg-hero py-16 sm:py-20 lg:py-24">
        <div className="container-narrow">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="text-center"
          >
            <h1 className="heading-display text-foreground mb-6">
              Pricing
            </h1>
            <p className="body-large text-muted-foreground max-w-2xl mx-auto">
              Transparent ranges based on scope. No surprises. No hidden fees.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="bg-background py-16 sm:py-20 lg:py-24">
        <div className="container-wide">
          <div className="grid md:grid-cols-3 gap-6">
            {pricingTiers.map((tier, i) => (
              <motion.div
                key={tier.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative"
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-accent text-accent-foreground">Most Popular</Badge>
                  </div>
                )}
                <Card className={`card-elevated h-full flex flex-col ${tier.popular ? 'ring-2 ring-accent' : ''}`}>
                  <CardHeader className="pb-4">
                    <CardTitle className="font-serif text-xl font-medium text-foreground">
                      {tier.title}
                    </CardTitle>
                    <CardDescription>{tier.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-1">
                    <div className="mb-5">
                      <p className="text-2xl font-serif font-medium text-foreground">{tier.range}</p>
                      <p className="text-sm text-muted-foreground">{tier.timeline}</p>
                    </div>
                    
                    <ul className="space-y-3 mb-5 flex-1">
                      {tier.includes.map((item, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="border-t border-border/50 pt-4 mt-auto">
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">Ideal for:</span> {tier.ideal}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition with Illustration */}
      <section className="bg-section py-16 sm:py-20">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Illustration */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
              className="flex justify-center"
            >
              <div className="w-full max-w-sm">
                <img
                  src={illustration}
                  alt="The value of clarity - from chaos to structure"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="bg-card border border-border/50 rounded-xl p-8"
            >
              <div className="flex items-start gap-4">
                <AlertCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-serif text-xl font-medium text-foreground mb-4">What You Are Really Paying For</h3>
                  <div className="space-y-3 text-muted-foreground">
                    <p>
                      <strong className="text-foreground">Not just deliverables.</strong> You are paying for clarity. For confidence. For knowing whether to go forward or pivot before you have burned through your runway.
                    </p>
                    <p>
                      <strong className="text-foreground">The real comparison is not us vs. cheaper.</strong> It is the cost of building the wrong thing, hiring the wrong team, or scaling confusion.
                    </p>
                    <p>
                      We have seen founders spend $100k+ on products nobody wanted. Early validation at 10% of that cost is one of the best investments you can make.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background py-16 sm:py-20">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="heading-section text-foreground">Common Questions</h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Accordion type="single" collapsible className="space-y-3">
              {faq.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-card border border-border/50 rounded-xl px-6">
                  <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline py-4">
                    <span className="flex items-center gap-3">
                      <HelpCircle className="h-4 w-4 text-accent flex-shrink-0" />
                      {item.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4 pl-7">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground py-16 sm:py-20">
        <div className="container-narrow text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-section mb-5">Ready to Talk?</h2>
            <p className="body-base text-primary-foreground/80 mb-6">
              Tell us what you are working on. We will give you a clear, honest quote.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button variant="secondary" size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full">
                <Link to="/contact">
                  Submit Your Wishlist
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full">
                <Link to="/engagements">See Engagement Types</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
